import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { CheckCircle, ArrowRight, Download, Mail } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMemo, useEffect } from "react";
import jsPDF from "jspdf";
import { usePaymentOrder } from "@/lib/api/hooks";
import { useSubscriptionPlans } from "@/components/PaymentForm";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";

const Success = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  
  // Extract orderId from URL query parameter or sessionStorage
  const urlOrderId = searchParams.get("orderId");
  const storedOrderId = sessionStorage.getItem("pendingPaymentOrderId");
  const orderId = urlOrderId || storedOrderId;
  
  // Clear stored orderId if we have it from URL
  useEffect(() => {
    if (urlOrderId && storedOrderId) {
      sessionStorage.removeItem("pendingPaymentOrderId");
    }
  }, [urlOrderId, storedOrderId]);
  
  // Fetch payment order details
  const { data: orderDetails, isLoading, isError } = usePaymentOrder(orderId);
  
  // Fetch subscription plans
  const { subscriptionPlans } = useSubscriptionPlans();
  
  // Get plan name from subscription plans
  const planName = useMemo(() => {
    if (!orderDetails?.subscriptionPlanName) return "N/A";
    if (!subscriptionPlans || subscriptionPlans.length === 0) {
      return orderDetails.subscriptionPlanName.charAt(0).toUpperCase() + orderDetails.subscriptionPlanName.slice(1);
    }
    const plan = subscriptionPlans.find(
      (p) => p.name.toLowerCase() === orderDetails.subscriptionPlanName.toLowerCase()
    );
    return plan ? plan.name : orderDetails.subscriptionPlanName.charAt(0).toUpperCase() + orderDetails.subscriptionPlanName.slice(1);
  }, [orderDetails, subscriptionPlans]);
  
  // Format date helper
  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'long',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  };
  
  // Format payment method
  const formatPaymentMethod = (paymentGroup: string | undefined) => {
    if (!paymentGroup) return "N/A";
    return paymentGroup.toUpperCase();
  };

  // Convert number to Indian Rupees text format
  const numberToWords = (num: number): string => {
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    
    if (num === 0) return 'Zero Rupees Only';
    
    const convertHundreds = (n: number): string => {
      let result = '';
      if (n >= 100) {
        result += ones[Math.floor(n / 100)] + ' Hundred ';
        n %= 100;
      }
      if (n >= 20) {
        result += tens[Math.floor(n / 10)] + ' ';
        n %= 10;
      }
      if (n > 0) {
        result += ones[n] + ' ';
      }
      return result.trim();
    };
    
    let remaining = num;
    let words = '';
    
    // Crores
    if (remaining >= 10000000) {
      const crores = Math.floor(remaining / 10000000);
      words += convertHundreds(crores) + ' Crore ';
      remaining %= 10000000;
    }
    
    // Lakhs
    if (remaining >= 100000) {
      const lakhs = Math.floor(remaining / 100000);
      words += convertHundreds(lakhs) + ' Lakh ';
      remaining %= 100000;
    }
    
    // Thousands
    if (remaining >= 1000) {
      const thousands = Math.floor(remaining / 1000);
      words += convertHundreds(thousands) + ' Thousand ';
      remaining %= 1000;
    }
    
    // Hundreds, Tens, Ones
    if (remaining > 0) {
      words += convertHundreds(remaining);
    }
    
    return words.trim() + ' Rupees Only';
  };

  const handleDownloadReceipt = () => {
    if (!orderDetails) {
      toast({
        title: "Error",
        description: "Order details not available",
        variant: "destructive",
      });
      return;
    }
    
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    const contentWidth = pageWidth - (margin * 2);
    
    // Helper function to check and add new page if needed
    const checkPageBreak = (requiredSpace: number) => {
      if (yPos + requiredSpace > pageHeight - 20) {
        doc.addPage();
        yPos = 20;
      }
    };
    
    const currentDate = formatDate(orderDetails.createdDate);
    const nextBillingDate = formatDate(orderDetails.nexttBillingDate);
    
    // ========== HEADER SECTION ==========
    // Colored header background - compact
    doc.setFillColor(0, 114, 181); // Primary blue #0072B5
    doc.rect(0, 0, pageWidth, 37, 'F');
    
    // Company Name - White text on blue background
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(15);
    doc.setFont(undefined, 'bold');
    doc.text('KIREETI SOFT TECHNOLOGIES LIMITED.', pageWidth / 2, 15, { align: 'center' });
    
    // Subtitle
    doc.setFontSize(7);
    doc.setTextColor(255, 255, 255);
    doc.setFont(undefined, 'normal');
    doc.text('Pharmasoft CS Director', pageWidth / 2, 21, { align: 'center' });
    
    // Receipt Badge - Compact
    doc.setFillColor(76, 175, 80); // Green for success
    doc.roundedRect(pageWidth / 2 - 30, 25, 60, 7, 3, 3, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(8);
    doc.setFont(undefined, 'bold');
    doc.text('PAYMENT RECEIPT', pageWidth / 2, 29, { align: 'center' });
    
    let yPos = 46;
    
    // ========== RECEIPT INFO SECTION ==========
    // Receipt info box with subtle border
    doc.setDrawColor(220, 220, 220);
    doc.setFillColor(252, 252, 252);
    doc.roundedRect(margin, yPos, contentWidth, 23, 3, 3, 'FD');
    
    yPos += 8;
    doc.setTextColor(120, 120, 120);
    doc.setFontSize(7);
    doc.setFont(undefined, 'normal');
    doc.text('Receipt Date', margin + 8, yPos);
    doc.text('Order ID', margin + contentWidth / 2 + 3, yPos);
    
    yPos += 7;
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(9);
    doc.setFont(undefined, 'bold');
    doc.text(currentDate, margin + 8, yPos);
    doc.setFontSize(8);
    doc.text(orderDetails.orderId, margin + contentWidth / 2 + 3, yPos);
    
    yPos += 10;
    
    // ========== CUSTOMER INFORMATION SECTION ==========
    doc.setFillColor(248, 249, 251);
    doc.roundedRect(margin, yPos, contentWidth, 27, 3, 3, 'F');
    
    yPos += 8;
    doc.setTextColor(0, 114, 181);
    doc.setFontSize(10);
    doc.setFont(undefined, 'bold');
    doc.text('Customer Information', margin + 8, yPos);
    
    yPos += 7;
    doc.setTextColor(80, 80, 80);
    doc.setFontSize(8);
    doc.setFont(undefined, 'normal');
    doc.text(`Name:`, margin + 8, yPos);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text(orderDetails.customerName, margin + 25, yPos);
    
    yPos += 6;
    doc.setFont(undefined, 'normal');
    doc.setTextColor(80, 80, 80);
    doc.text(`Email:`, margin + 8, yPos);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text(orderDetails.customerEmail, margin + 25, yPos);
    
    yPos += 6;
    doc.setFont(undefined, 'normal');
    doc.setTextColor(80, 80, 80);
    doc.text(`Phone:`, margin + 8, yPos);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text(orderDetails.customerPhone, margin + 25, yPos);
    
    yPos += 10;
    
    // ========== TRANSACTION DETAILS SECTION ==========
    // Calculate height based on compact spacing: title(7) + spacing(6) + rows(6*6) + padding(7) = 49
    const transactionDetailsHeight = 49;
    doc.setFillColor(248, 249, 251);
    doc.roundedRect(margin, yPos, contentWidth, transactionDetailsHeight, 3, 3, 'F');
    
    yPos += 7;
    doc.setTextColor(0, 114, 181);
    doc.setFontSize(10);
    doc.setFont(undefined, 'bold');
    doc.text('Transaction Details', margin + 8, yPos);
    
    yPos += 6;
    
    // Transaction details with compact spacing
    const details = [
      { label: 'Subscription Plan', value: planName },
      { label: 'Payment Method', value: formatPaymentMethod(orderDetails.paymentGroup) },
      { label: 'Payment Status', value: orderDetails.paymentStatus, color: [76, 175, 80] }, // Green for success
      { label: 'Transaction ID', value: orderDetails.transactionId || 'N/A' },
      { label: 'Transaction Date', value: currentDate },
      { label: 'Next Billing Date', value: nextBillingDate !== 'N/A' ? nextBillingDate : 'N/A' },
    ];
    
    details.forEach((detail, index) => {
      const rowY = yPos + (index * 6); // Increased spacing to 6 for better readability
      
      // Label
      doc.setTextColor(100, 100, 100);
      doc.setFontSize(8);
      doc.setFont(undefined, 'normal');
      doc.text(detail.label + ':', margin + 8, rowY);
      
      // Value
      const color = detail.color || [0, 0, 0];
      doc.setTextColor(color[0], color[1], color[2]);
      doc.setFontSize(8);
      doc.setFont(undefined, 'bold');
      const valueX = margin + contentWidth - 8;
      doc.text(detail.value, valueX, rowY, { align: 'right' });
    });
    
    // Move to next section
    yPos += (details.length * 6) + 7;
    
    // ========== AMOUNT HIGHLIGHT SECTION ==========
    // Professional blue bar with rounded corners - compact design
    const amountBarHeight = 26;
    doc.setFillColor(0, 114, 181); // Primary blue #0072B5
    doc.roundedRect(margin, yPos, contentWidth, amountBarHeight, 4, 4, 'F');
    
    const barStartY = yPos;
    
    // First line: Label and Amount
    yPos = barStartY + 8;
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(11);
    doc.setFont(undefined, 'normal');
    doc.text('Total Amount Paid', margin + 10, yPos);
    
    // Right side - Amount (large and bold)
    // Format amount properly without currency symbol encoding issues
    // Ensure decimals are always shown with 2 decimal places
    const amountValue = parseFloat(orderDetails.amount.toString());
    const formattedAmount = amountValue.toLocaleString('en-IN', { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2,
      useGrouping: true
    });
    // Use "Rs." instead of ₹ to avoid font encoding issues in jsPDF
    const amountText = `Rs. ${formattedAmount}`;
    doc.setFontSize(20);
    doc.setFont(undefined, 'bold');
    doc.text(amountText, margin + contentWidth - 10, yPos, { align: 'right' });
    
    // Second line: Amount in words
    yPos = barStartY + 19;
    const amountInWords = numberToWords(Math.floor(orderDetails.amount));
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(9);
    doc.setFont(undefined, 'normal');
    // Split long text if needed to fit within the bar
    const maxWidth = contentWidth - 20;
    const wordsLines = doc.splitTextToSize(amountInWords, maxWidth);
    wordsLines.forEach((line: string, index: number) => {
      doc.text(line, margin + 7, yPos + (index * 5), { align: 'left' });
    });
    
    yPos = barStartY + amountBarHeight + 9;
    
    // ========== FOOTER SECTION ==========
    // Top border - subtle line
    doc.setDrawColor(230, 230, 230);
    doc.line(margin, yPos, margin + contentWidth, yPos);
    
    yPos += 7;
    
    // Payment Successful - prominent heading
    doc.setTextColor(0, 114, 181);
    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.text('Payment Successful', pageWidth / 2, yPos, { align: 'center' });
    
    yPos += 6;
    
    // Thin separator line
    doc.setDrawColor(230, 230, 230);
    doc.line(margin + 40, yPos, margin + contentWidth - 40, yPos);
    
    yPos += 6;
    
    // Thank you message
    doc.setTextColor(0, 114, 181);
    doc.setFontSize(9);
    doc.setFont(undefined, 'bold');
    doc.text('Thank you for your payment!', pageWidth / 2, yPos, { align: 'center' });
    
    yPos += 6;
    doc.setTextColor(120, 120, 120);
    doc.setFontSize(7);
    doc.setFont(undefined, 'normal');
    const disclaimerText = 'This is a computer-generated receipt and does not require a signature.';
    // Split long text if needed to fit within page width
    const disclaimerLines = doc.splitTextToSize(disclaimerText, contentWidth - 30);
    disclaimerLines.forEach((line: string, index: number) => {
      doc.text(line, pageWidth / 2, yPos + (index * 5), { align: 'center' });
    });
    yPos += (disclaimerLines.length * 5);
    
    yPos += 4;
    doc.setFontSize(7);
    const contactText = 'For any queries, please contact support@kireetisoft.com';
    // Split contact text if needed
    const contactLines = doc.splitTextToSize(contactText, contentWidth - 30);
    contactLines.forEach((line: string, index: number) => {
      doc.text(line, pageWidth / 2, yPos + (index * 5), { align: 'center' });
    });
    yPos += (contactLines.length * 5);
    
    // Bottom border
    yPos += 5;
    doc.setDrawColor(230, 230, 230);
    doc.line(margin, yPos, margin + contentWidth, yPos);
    
    // Page number and footer info - subtle
    yPos += 5;
    doc.setFontSize(6);
    doc.setTextColor(160, 160, 160);
    const generatedText = `Generated on ${new Date().toLocaleString('en-IN')}`;
    doc.text(generatedText, pageWidth / 2, yPos, { align: 'center' });
    
    // Save PDF
    doc.save(`Receipt-${orderDetails.orderId}.pdf`);
  };
  
  // Show loading state
  if (isLoading) {
    return (
      <Layout>
        <div className="min-h-screen bg-background">
          <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 lg:py-16">
            <div className="max-w-lg mx-auto text-center">
              <Skeleton className="w-24 h-24 rounded-full mx-auto mb-6" />
              <Skeleton className="h-8 w-64 mx-auto mb-4" />
              <Skeleton className="h-4 w-96 mx-auto mb-8" />
              <Skeleton className="h-64 w-full mb-6" />
            </div>
          </main>
        </div>
      </Layout>
    );
  }
  
  // Show error state
  if (isError || !orderDetails) {
    return (
      <Layout>
        <div className="min-h-screen bg-background">
          <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 lg:py-16">
            <div className="max-w-lg mx-auto text-center">
              <h1 className="text-2xl sm:text-3xl font-bold text-foreground mb-4">Error Loading Order Details</h1>
              <p className="text-muted-foreground mb-6">
                {!orderId ? "No order ID provided in URL" : "Failed to load order details. Please try again."}
              </p>
              <Button onClick={() => navigate("/dashboard")} className="gradient-primary">
                Go to Dashboard
              </Button>
            </div>
          </main>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-background">
        <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 lg:py-16">
          <div className="max-w-lg mx-auto text-center">
            <div className="relative mb-6 sm:mb-8 flex items-center justify-center">
              <div className="absolute w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 rounded-full bg-primary/20 animate-ping" />
              <div className="absolute w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 rounded-full bg-primary/30 animate-pulse" />
              <div className="relative w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center shadow-lg shadow-primary/25">
                <CheckCircle className="w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 text-white" />
              </div>
            </div>

            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground mb-2 sm:mb-3">Payment Successful!</h1>
            <p className="text-sm sm:text-base lg:text-lg text-muted-foreground mb-6 sm:mb-8 px-4">
              Thank you for your payment. Your subscription has been renewed successfully.
            </p>

            <div className="bg-card rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-card border border-border mb-6 sm:mb-8 text-left">
              <h2 className="text-sm sm:text-base font-semibold text-foreground mb-3 sm:mb-4">Transaction Details</h2>
              <div className="space-y-2 sm:space-y-3">
                <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-2">
                  <span className="text-xs sm:text-sm text-muted-foreground">Order ID</span>
                  <span className="text-xs sm:text-sm font-medium text-foreground break-all">#{orderDetails.orderId}</span>
                </div>
                <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-2">
                  <span className="text-xs sm:text-sm text-muted-foreground">Plan</span>
                  <span className="text-xs sm:text-sm font-medium text-foreground">{planName}</span>
                </div>
                <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-2">
                  <span className="text-xs sm:text-sm text-muted-foreground">Amount Paid</span>
                  <span className="text-xs sm:text-sm font-medium text-foreground">
                    ₹{orderDetails.amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
                <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-2">
                  <span className="text-xs sm:text-sm text-muted-foreground">Payment Method</span>
                  <span className="text-xs sm:text-sm font-medium text-foreground">{formatPaymentMethod(orderDetails.paymentGroup)}</span>
                </div>
                <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-2">
                  <span className="text-xs sm:text-sm text-muted-foreground">Payment Status</span>
                  <span className="text-xs sm:text-sm font-medium text-foreground">{orderDetails.paymentStatus}</span>
                </div>
                <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-2">
                  <span className="text-xs sm:text-sm text-muted-foreground">Transaction ID</span>
                  <span className="text-xs sm:text-sm font-medium text-foreground break-all">{orderDetails.transactionId || "N/A"}</span>
                </div>
                {orderDetails.nexttBillingDate && (
                  <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-2">
                    <span className="text-xs sm:text-sm text-muted-foreground">Next Billing Date</span>
                    <span className="text-xs sm:text-sm font-medium text-foreground">{formatDate(orderDetails.nexttBillingDate)}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-2 sm:space-y-3">
              <Button onClick={() => navigate("/dashboard")} className="w-full gradient-primary hover:opacity-90 transition-opacity h-11 sm:h-12 text-sm sm:text-base">
                Go to Dashboard
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                <Button variant="outline" className="w-full sm:flex-1 text-sm sm:text-base" onClick={handleDownloadReceipt}>
                  <Download className="w-4 h-4 mr-2" />
                  <span className="hidden sm:inline">Download Receipt</span>
                  <span className="sm:hidden">Download</span>
                </Button>
                <Button variant="outline" className="w-full sm:flex-1 text-sm sm:text-base">
                  <Mail className="w-4 h-4 mr-2" />
                  <span className="hidden sm:inline">Email Receipt</span>
                  <span className="sm:hidden">Email</span>
                </Button>
              </div>
            </div>

            <p className="text-xs sm:text-sm text-muted-foreground mt-6 sm:mt-8 px-4">
              A confirmation email has been sent to <span className="text-foreground break-all">{orderDetails.customerEmail}</span>. If you have any questions, please contact our support
              team.
            </p>
          </div>
        </main>
      </div>
    </Layout>
  );
};

export default Success;

